import React, { createContext, useContext, useReducer, useEffect } from 'react';
import api from '../utils/api';

const CRMContext = createContext();

const initialState = {
  customers: [],
  currentCustomer: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPrevPage: false
  }
};

const crmReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
        error: null
      };
    case 'SET_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case 'SET_CUSTOMERS':
      return {
        ...state,
        customers: action.payload.customers,
        pagination: action.payload.pagination,
        loading: false,
        error: null
      };
    case 'SET_CURRENT_CUSTOMER':
      return {
        ...state,
        currentCustomer: action.payload,
        loading: false,
        error: null
      };
    case 'ADD_CUSTOMER':
      return {
        ...state,
        customers: [action.payload, ...state.customers],
        loading: false,
        error: null
      };
    case 'UPDATE_CUSTOMER':
      return {
        ...state,
        customers: state.customers.map(customer => 
          customer.id === action.payload.id ? action.payload : customer
        ),
        currentCustomer: action.payload.id === state.currentCustomer?.id ? 
          action.payload : state.currentCustomer,
        loading: false,
        error: null
      };
    case 'DELETE_CUSTOMER':
      return {
        ...state,
        customers: state.customers.filter(customer => 
          customer.id !== action.payload
        ),
        currentCustomer: state.currentCustomer?.id === action.payload ? 
          null : state.currentCustomer,
        loading: false,
        error: null
      };
    default:
      return state;
  }
};

export const CRMProvider = ({ children }) => {
  const [state, dispatch] = useReducer(crmReducer, initialState);

  // Get all customers for a location
  const getCustomers = async (locationId, page = 1, limit = 20, search = '', segment = '') => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const params = new URLSearchParams();
      params.append('locationId', locationId);
      params.append('page', page);
      params.append('limit', limit);
      
      if (search) params.append('search', search);
      if (segment) params.append('segment', segment);
      
      const response = await api.get(`/customers?${params.toString()}`);
      
      if (response.data.success) {
        dispatch({ 
          type: 'SET_CUSTOMERS', 
          payload: response.data.data 
        });
      } else {
        dispatch({ 
          type: 'SET_ERROR', 
          payload: response.data.message || 'Failed to fetch customers' 
        });
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error.response?.data?.message || error.message || 'Failed to fetch customers' 
      });
    }
  };

  // Get single customer
  const getCustomer = async (id) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await api.get(`/customers/${id}`);
      
      if (response.data.success) {
        dispatch({ 
          type: 'SET_CURRENT_CUSTOMER', 
          payload: response.data.data.customer 
        });
        return response.data.data.customer;
      } else {
        dispatch({ 
          type: 'SET_ERROR', 
          payload: response.data.message || 'Failed to fetch customer' 
        });
        return null;
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error.response?.data?.message || error.message || 'Failed to fetch customer' 
      });
      return null;
    }
  };

  // Create new customer
  const createCustomer = async (customerData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await api.post('/customers', customerData);
      
      if (response.data.success) {
        dispatch({ 
          type: 'ADD_CUSTOMER', 
          payload: response.data.data.customer 
        });
        return { success: true, customer: response.data.data.customer };
      } else {
        dispatch({ 
          type: 'SET_ERROR', 
          payload: response.data.message || 'Failed to create customer' 
        });
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error.response?.data?.message || error.message || 'Failed to create customer' 
      });
      return { success: false, message: error.response?.data?.message || error.message };
    }
  };

  // Update customer
  const updateCustomer = async (id, customerData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await api.put(`/customers/${id}`, customerData);
      
      if (response.data.success) {
        dispatch({ 
          type: 'UPDATE_CUSTOMER', 
          payload: response.data.data.customer 
        });
        return { success: true, customer: response.data.data.customer };
      } else {
        dispatch({ 
          type: 'SET_ERROR', 
          payload: response.data.message || 'Failed to update customer' 
        });
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error.response?.data?.message || error.message || 'Failed to update customer' 
      });
      return { success: false, message: error.response?.data?.message || error.message };
    }
  };

  // Delete customer
  const deleteCustomer = async (id) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await api.delete(`/customers/${id}`);
      
      if (response.data.success) {
        dispatch({ 
          type: 'DELETE_CUSTOMER', 
          payload: id 
        });
        return { success: true };
      } else {
        dispatch({ 
          type: 'SET_ERROR', 
          payload: response.data.message || 'Failed to delete customer' 
        });
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error.response?.data?.message || error.message || 'Failed to delete customer' 
      });
      return { success: false, message: error.response?.data?.message || error.message };
    }
  };

  return (
    <CRMContext.Provider
      value={{
        ...state,
        getCustomers,
        getCustomer,
        createCustomer,
        updateCustomer,
        deleteCustomer
      }}
    >
      {children}
    </CRMContext.Provider>
  );
};

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (!context) {
    throw new Error('useCRM must be used within a CRMProvider');
  }
  return context;
};
